import { useNavigate } from "react-router-dom";
import { Hourglass, Mail, LogOut } from "lucide-react";
import { Button } from "@/components/ui/button";
import { NovatusLogo } from "@/components/NovatusLogo";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Waiting room shown after a self-registration that did NOT pass the
 * automatic plausibility check. The account is in `pending_admin` status
 * and an administrator must approve it before the user receives the Stripe
 * checkout link via email.
 */
export default function RegisterPendingAdmin() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#09090B] flex flex-col items-center justify-center px-6 py-10">
      <div className="w-full max-w-md text-center">
        <div className="mb-6 flex justify-center"><NovatusLogo size="md" /></div>

        <div
          data-testid="pending-admin-card"
          className="bg-[#18181B] border border-amber-500/30 rounded-2xl p-8 shadow-2xl"
        >
          <div className="w-16 h-16 rounded-full bg-amber-500/15 flex items-center justify-center mx-auto mb-5">
            <Hourglass className="text-amber-400" size={32} />
          </div>

          <h1 className="font-display text-2xl font-semibold mb-2">
            {user?.first_name ? `Vielen Dank, ${user.first_name}!` : "Vielen Dank!"}
          </h1>

          <p className="text-sm text-zinc-300 leading-relaxed mb-5">
            Ihre Registrierung ist eingegangen. Unser Team prüft Ihre Daten
            manuell und schaltet das Konto in Kürze frei.
          </p>

          <div className="text-left bg-[#09090B] border border-zinc-800 rounded-xl p-4 mb-5">
            <div className="flex items-start gap-3">
              <Mail className="text-[#00E575] mt-0.5 shrink-0" size={18} />
              <div>
                <p className="text-sm font-semibold text-zinc-100 mb-1">
                  Was passiert als nächstes?
                </p>
                <p className="text-xs text-zinc-400 leading-relaxed">
                  Sie erhalten eine E-Mail an{" "}
                  <span className="font-mono text-zinc-200">{user?.email || "Ihre Adresse"}</span>
                  {" "}sobald wir Ihre Anfrage geprüft haben. Die E-Mail enthält
                  einen Link zur Bezahlung — danach steht Ihnen Sales Pro voll
                  zur Verfügung.
                </p>
              </div>
            </div>
          </div>

          <p className="text-xs text-zinc-300 mb-5">
            Die Prüfung erfolgt in der Regel innerhalb eines Werktags.
          </p>

          <Button
            onClick={handleLogout}
            variant="outline"
            data-testid="pending-admin-logout-btn"
            className="w-full border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          >
            <LogOut size={16} className="mr-2" /> Abmelden
          </Button>
        </div>

        <p className="mt-6 text-xs text-zinc-300 text-center max-w-xs mx-auto leading-relaxed">
          Fragen? Schreiben Sie an{" "}
          <a href="mailto:support@novatus-energie.de" className="text-[#00E575] hover:underline">
            support@novatus-energie.de
          </a>
        </p>
      </div>
    </div>
  );
}
